import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostBinding,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewEncapsulation,
} from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { DetectDevicesService } from '../fi-shared-services';
import { IChatConversation, INewConversationData, IOpenConversationManageUsersData } from './fi-chat';
import { FiChatService } from './fi-chat.service';
import { FicScreenService } from '@fi-shared/fic-screen/fic-screen.service';
import { FiChatConversationListScreenComponent } from '@fi-shared/fi-chat/fi-chat-conversation-list/fi-chat-conversation-list-screen/fi-chat-conversation-list-screen.component';
import {
    FiChatConversationScreenComponent,
    FiChatConversationScreenData,
} from '@fi-shared/fi-chat/fi-chat-conversation/fi-chat-conversation-screen/fi-chat-conversation-screen.component';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { FiChatConversationType } from '@fi-shared/fi-chat/fi-chat-conversation-type';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import { takeUntil } from 'rxjs/operators';
import { isDefined, isEqual } from '@fi-shared/utils';
import { ILightWeightUser, ILightWeightUserWithUnits } from '@fi-shared/models';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { ManageUsersStep } from '@fi-shared/fi-chat/fi-chat-conversation-manage-type';
import { FiChatSyncService } from '@fi-shared/fi-chat/fi-chat-sync.service';
import { MobileAppHelperBaseService } from '@fi-shared/fi-shared-services/mobile-app-helper-base.service';

const CONVERSATION_CONTAINER_ID = 'conversation-container';
const CONVERSATION_MARGIN_RIGHT = 100;
const CONVERSATION_ADDITIONAL_WIDTH = 15;

interface IOpenConversationOptions {
    managingUsersMode?: boolean;
    manageUsersStep?: ManageUsersStep;
}

@Component({
    selector: 'fi-chat',
    template: `
        @if (!isMobileDevice()) {
            <div></div>
            @for (conversation of activeConversations$ | async; track conversation) {
                <fi-chat-conversation-container
                    [conversation]="conversation"
                    [id]="getConversationContainerId(conversation)"
                    (closeConversation)="onCloseConversation($event)"
                    (startNewConversation)="onStartNewConversation($event)"
                    [managingUsersMode]="isManageUsersMode(conversation)"
                    [currentManagingUsersStep]="getCurrentManagingUsersStep(conversation)"
                />
            }
            <fi-chat-conversation-list-container
                [ngClass]="{ shown: (showConversationList$ | async) }"
                (openExistingConversation)="onOpenConversation($event)"
                (openNewConversation)="onOpenNewConversation($event)"
                (closeConversationList)="onCloseConversationList()"
                (createNewGroupConversation)="onStartNewConversation($event)"
                (closeConversation)="onCloseConversation($event)"
                (openManageUsersConversation)="onOpenManageUsersConversation($event)"
            />
        }
    `,
    styleUrls: ['./fi-chat.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-chat' },
    standalone: false,
})
export class FiChatComponent implements OnInit, OnDestroy {
    activeConversations$ = new BehaviorSubject<IChatConversation[]>([]);
    userManageConversationData: { conversationId: number; currentManagingStep: ManageUsersStep };

    private maxActiveConversations = 0;
    private conversationWrapperWidth: number;
    get showConversationList$() {
        return this.fiChatHelperService.showConversationList$;
    }
    @HostBinding('class.shown')
    get isChatShown() {
        return this.activeConversations$.value.length || this.isConversationListShown();
    }

    constructor(
        private detectDevices: DetectDevicesService,
        private fiChatService: FiChatService,
        private fiChatHelperService: FiChatHelperService,
        private fiChatSyncService: FiChatSyncService,
        private ficScreenService: FicScreenService,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private renderer2: Renderer2,
        private elementRef: ElementRef,
        private fiBrowserEventsService: FiBrowserEventsService,
        private mobileAppHelperService: MobileAppHelperBaseService,
    ) {}

    ngOnInit(): void {
        this.fiChatHelperService.onConversationListOpening$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            // open conversations list
            this.checkOpenConversationListScreen();
            this.fiChatHelperService.openConversationList();

            // commented out: https://chainformation.atlassian.net/browse/FI-5889
            /*// should mark all conversations as read and clear badge
                this.fiChatService.markAllConversationsAsRead();*/

            this.animateConversationContainers();
        });

        this.conversationWrapperWidth =
            this.elementRef.nativeElement.offsetWidth +
            +getComputedStyle(this.elementRef.nativeElement).marginRight.slice(0, -2);
        this.setWindowResizeListener();
        this.setMaxActiveConversations();

        //
        this.activeConversations$.subscribe((x) => {
            this.animateConversationContainers();
            this.fiChatHelperService.setActiveConversationsCount(x.length);
        });

        this.fiChatService.onOpenConversationById$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((conversationId) => {
            const conversation = this.fiChatSyncService.findConversationById(conversationId);
            this.onOpenConversation(conversation);
        });

        this.fiChatService.onOpenNewConversationByUser$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((user: ILightWeightUserWithUnits) => this.onOpenNewConversation(user));

        this.fiChatHelperService.showForwardMessageConversationList$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => {
                const forwardMessageConversation: IChatConversation = this.activeConversations$.value.find(
                    (conversation) => {
                        return conversation.Type === FiChatConversationType.Forward;
                    },
                );
                if (isDefined(forwardMessageConversation)) {
                    this.onCloseConversation(forwardMessageConversation);
                    // we use setTimeout here because it is set in onCloseConversation method
                    setTimeout(
                        () => this.onOpenConversation(this.fiChatHelperService.getForwardMessageConversationInstance()),
                        200,
                    );
                } else {
                    this.onOpenConversation(this.fiChatHelperService.getForwardMessageConversationInstance());
                }
            });
        this.fiChatHelperService.closeForwardMessageConversationList$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => {
                this.closeForwardMessageConversation();
            });
        this.fiChatHelperService.showForwardMessageConversation$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((conversation: IChatConversation) => {
                this.closeForwardMessageConversation();
                // we use setTimeout here because it is set in onCloseConversation method
                setTimeout(() => this.onOpenConversation(conversation), 200);
            });
        this.fiChatHelperService.showForwardMessageNewConversation$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((forwardedMessage) => {
                this.closeForwardMessageConversation();
                // we use setTimeout here because it is set in onCloseConversation method
                setTimeout(() => {
                    const newConversation = this.fiChatHelperService.getNewConversationInstance(
                        forwardedMessage.targetUser,
                    );
                    this.onOpenConversation(newConversation);
                }, 200);
            });
    }

    private closeForwardMessageConversation() {
        const forwardMessageConversation: IChatConversation = this.activeConversations$.value.find(
            (conversation: IChatConversation) => {
                return conversation.Type === FiChatConversationType.Forward;
            },
        );
        this.onCloseConversation(forwardMessageConversation);
    }

    isConversationListShown(): boolean {
        return this.fiChatHelperService.isConversationListOpened();
    }

    setWindowResizeListener() {
        this.fiBrowserEventsService
            .getWindowEventSubject$('resize')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => {
                this.setMaxActiveConversations();
            });
    }

    setMaxActiveConversations() {
        const tempAmount = this.maxActiveConversations;
        this.maxActiveConversations = Math.floor(
            (this.elementRef.nativeElement.parentElement.offsetWidth -
                this.elementRef.nativeElement.offsetWidth -
                +getComputedStyle(this.elementRef.nativeElement).marginRight.slice(0, -2)) /
                this.conversationWrapperWidth,
        );

        if (tempAmount !== this.maxActiveConversations) this.maxActiveConversationsChange();
    }

    maxActiveConversationsChange() {
        if (this.activeConversations$.value.length > this.maxActiveConversations) {
            for (let i = 0; i < this.activeConversations$.value.length - this.maxActiveConversations; i++) {
                this.onCloseConversation(this.activeConversations$.value[i]);
            }
        }
    }

    isMobileDevice() {
        return this.detectDevices.isMobilePhone() || this.isMobileAppMode();
    }

    isMobileAppMode() {
        return this.mobileAppHelperService.mobileAppMode;
    }

    isManageUsersMode(conversation: IChatConversation): boolean {
        return !!this.userManageConversationData && this.userManageConversationData.conversationId === conversation.Id;
    }

    getCurrentManagingUsersStep(conversation: IChatConversation): ManageUsersStep {
        return !!this.userManageConversationData && this.userManageConversationData.conversationId === conversation.Id
            ? this.userManageConversationData.currentManagingStep
            : undefined;
    }

    /// region Conversation List
    private isConversationListScreenOpened = false;
    private checkOpenConversationListScreen() {
        if (this.isMobileDevice() && !this.isConversationListScreenOpened) {
            this.isConversationListScreenOpened = true;
            // open messaging screen
            const screenRef = this.ficScreenService.openScreen({
                componentType: FiChatConversationListScreenComponent,
                data: {
                    onOpenExistingConversation: (conversation: IChatConversation) =>
                        this.onOpenConversation(conversation),
                    onOpenNewConversation: (user: ILightWeightUser) => this.onOpenNewConversation(user),
                    onCreateNewGroupConversation: (newConversationData: INewConversationData) =>
                        this.onStartNewConversation(newConversationData),
                    onOpenManageUsersConversation: (data: IOpenConversationManageUsersData) =>
                        this.onOpenManageUsersConversation(data),
                },
            });
            screenRef.afterClosed.subscribe(() => {
                this.isConversationListScreenOpened = false;
                this.onCloseConversationList();
            });
        }
    }
    onCloseConversationList() {
        this.fiChatHelperService.closeConversationList();
        this.animateConversationContainers();
    }
    /// endregion

    openForwardMessageConversation(conversation: IChatConversation) {}

    closeForwardMessageConversationList() {
        this.fiChatHelperService.closeForwardMessageConversationList();
        this.animateConversationContainers();
    }

    /// region Existing Conversation
    onOpenManageUsersConversation(data: IOpenConversationManageUsersData) {
        this.onOpenConversation(data.conversation, { managingUsersMode: true, manageUsersStep: data.currentStep });
        this.userManageConversationData = {
            conversationId: data.conversation.Id,
            currentManagingStep: data.currentStep,
        };
    }

    onOpenConversation(conversation: IChatConversation, opts?: IOpenConversationOptions) {
        if (!isDefined(conversation)) return;

        if (!isDefined(opts)) {
            opts = {};
        }

        const activeConversations = this.activeConversations$.value;
        const isConversationAlreadyOpened = !!activeConversations.find((item) => item.Id === conversation.Id);
        if (isConversationAlreadyOpened) return;

        this.checkOpenConversationScreen(conversation, opts);

        // don't open more than maxActiveConversations conversations
        if (activeConversations.length >= this.maxActiveConversations) {
            activeConversations.splice(0, 1);
        }
        activeConversations.push(conversation);
        this.activeConversations$.next(activeConversations);
    }

    // we should store new conversation screen data for futher replacing conversation (in onStartNewConversation)
    private lastNewConversationData: FiChatConversationScreenData;
    private checkOpenConversationScreen(conversation: IChatConversation, opts: IOpenConversationOptions) {
        if (this.isMobileDevice()) {
            const conversationData: FiChatConversationScreenData = {
                conversation$: new BehaviorSubject<IChatConversation>(null),
                onStartNewConversation: (newConversationData) => this.onStartNewConversation(newConversationData),
                managingUsersMode: opts.managingUsersMode,
                manageUsersStep: opts.manageUsersStep,
            };

            let conversationToClose = conversation;
            conversationData.conversation$.subscribe((v) => (conversationToClose = v));
            conversationData.conversation$.next(conversation);

            // open conversation screen
            const screenRef = this.ficScreenService.openScreen({
                componentType: FiChatConversationScreenComponent,
                data: conversationData,
            });
            if (this.fiChatHelperService.isConversationNew(conversation))
                this.lastNewConversationData = conversationData;

            screenRef.afterClosed.subscribe(() => {
                this.onCloseConversation(conversationToClose);
                conversationData.conversation$.complete();
            });
        }
    }
    onCloseConversation($event: IChatConversation) {
        const activeConversations = this.activeConversations$.value;
        const idx = activeConversations.indexOf($event);
        if (idx !== -1) {
            if (this.isManageUsersMode($event)) this.userManageConversationData = undefined;

            this.animateConversationContainers(idx);
            // wait until animation finished
            setTimeout(() => {
                activeConversations.splice(idx, 1);
                this.activeConversations$.next(activeConversations);
            }, 200);
        }
    }
    /// endregion

    /// region New Conversation
    onOpenNewConversation(user: ILightWeightUser) {
        const newConversation = this.fiChatHelperService.getNewConversationInstance(user);
        this.onOpenConversation(newConversation);
    }

    onStartNewConversation(newConversationData: INewConversationData) {
        const existingConversation = this.fiChatHelperService.findConversationBySelectedUserIds(
            newConversationData.selectedUserIds,
        );
        /*const findExistingOneToOneConversationPromise = new Promise<IChatConversation>((resolve, reject) => {
            // we should check if 1-1 conversation already exists;
            // also user can have group chat with only 2 participants
            if (newConversationData.type === FiChatConversationType.OneToOne) {
                let existingOneToOneConversation = this.fiChatHelperService.findOneToOneConversation(
                    newConversationData.selectedUserIds,
                );
                if (existingOneToOneConversation) {
                    resolve(existingOneToOneConversation);
                } else {
                    // https://chainformation.atlassian.net/browse/FI-4200
                    // try to find existing conversation on backend
                    this.fiChatService
                        .findExistingConversations(newConversationData.selectedUserIds, newConversationData.type)
                        .then((result) => {
                            if (result.length) {
                                resolve(result[0]);
                            } else {
                                resolve(null);
                            }
                        }, reject);
                }
            } else {
                resolve(null);
            }
        });*/

        const startConversationPromise = new Promise<IChatConversation>((resolve, reject) => {
            //findExistingOneToOneConversationPromise.then((existingConversation) => {
            if (existingConversation) {
                resolve(existingConversation);
            } else {
                // ask backend to create new conversation
                this.fiChatService
                    .createNewConversation(newConversationData.selectedUserIds, newConversationData.type)
                    .then((newConversation) => resolve(newConversation), reject);
            }
            //});
        });

        startConversationPromise.then((conversation) => {
            if (newConversationData.message) {
                if (!conversation.LatestActivityMessage) {
                    conversation.LatestActivityMessage = newConversationData.message;
                } else {
                    conversation.LatestActivityMessage.Content = newConversationData.message.Content;
                }
                this.fiChatService.saveConversationMessage(conversation.Id, newConversationData.message).then(() => {
                    this.updateActiveConversations(conversation);
                });
            } else {
                this.updateActiveConversations(conversation);
            }
        });
    }

    private updateActiveConversations(conversation: IChatConversation) {
        // close all already opened 1-1 conversations with selected user
        const activeConversations = this.activeConversations$.value.filter((item) => item.Id !== conversation.Id);
        const newConversationIdx = activeConversations.findIndex((item) =>
            this.fiChatHelperService.isConversationNew(item),
        );
        if (newConversationIdx !== -1) {
            // update new conversation (replace with existing or created on backend)
            activeConversations[newConversationIdx] = conversation;
            if (this.lastNewConversationData) {
                this.lastNewConversationData.conversation$.next(conversation);
            }
            this.activeConversations$.next(activeConversations);
        } else {
            // open new conversation
            this.onOpenConversation(conversation);
        }
    }
    /// endregion

    onOpenForwardMessageConversation() {
        const forwardConversation = this.fiChatHelperService.getForwardMessageConversationInstance();
        this.onOpenConversation(forwardConversation);
    }

    getConversationContainerId(conversation: IChatConversation): string {
        const conversationContainerIndex = this.activeConversations$.value.findIndex((activeConversation) => {
            return isEqual(activeConversation, conversation);
        });

        return CONVERSATION_CONTAINER_ID + (conversationContainerIndex + 1);
    }

    private animateConversationContainers(containerIndex?: number) {
        // timeout is here for a reason that we need to animate container after it appears on the page.
        // if we just set transition delay in css, it won't be applied, because element appears and gets it's id immediately
        // so that style properties won't be changed - animation won't be applied.
        setTimeout(() => {
            if (this.activeConversations$.value.length) {
                if (containerIndex === undefined) {
                    this.activeConversations$.value.forEach((activeConversation) => {
                        const conversationContainerId = this.getConversationContainerId(activeConversation);
                        const conversationContainerElement = document.getElementById(conversationContainerId);
                        const conversationContainerIdNumber = +conversationContainerId.replace(/\D/g, '');

                        if (conversationContainerElement) {
                            this.renderer2.setStyle(conversationContainerElement, 'bottom', '0');
                            this.renderer2.setStyle(conversationContainerElement, 'height', '100%');
                            this.renderer2.setStyle(
                                conversationContainerElement,
                                'right',
                                this.getConversationContainerRightOffset(conversationContainerIdNumber),
                            );
                        }
                    });
                } else {
                    const conversationContainerId = this.getConversationContainerId(
                        this.activeConversations$.value[containerIndex],
                    );
                    const conversationContainerElement = document.getElementById(conversationContainerId);
                    if (conversationContainerElement) {
                        this.renderer2.setStyle(conversationContainerElement, 'bottom', '-370px');
                        this.renderer2.setStyle(conversationContainerElement, 'height', '0');
                    }
                }
            }
        }, 100);
    }

    private getConversationContainerRightOffset(containerId: number): string {
        let rightOffset =
            this.conversationWrapperWidth * containerId + CONVERSATION_ADDITIONAL_WIDTH * (containerId - 1);
        if (!this.isConversationListShown()) {
            rightOffset += -this.conversationWrapperWidth + CONVERSATION_MARGIN_RIGHT;
        }
        return `${rightOffset}px`;
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
        this.fiChatHelperService.closeConversationList();
    }
}
